'use client';
// CQI-5 — les contrôles internes d'un automate, depuis sa fiche.
//
// ── Une seule dérivation de l'état, pas deux ────────────────────────────────
//
// Cet onglet appelle la MÊME route que l'écran CQI (`GET ged/cqi-cartes`), avec
// l'équipement en paramètre. Il ne recompte rien et ne requalifie rien : le
// statut affiché ici est celui que produit `etatCarteCqi` côté moteur, le seul
// endroit du dépôt où l'état d'une carte se calcule.
//
// C'est le choix déjà fait pour l'onglet « Calibrations » et l'onglet
// « Maintenance » de la même fiche, et pour la même raison : deux écrans qui
// dérivent séparément le même verdict finissent par ne plus dire la même chose,
// et c'est celui qu'on consulte à la paillasse qui devient faux.
//
// ── Un état, pas un compteur ───────────────────────────────────────────────
//
// « 25 cartes » ne renseigne sur rien. Ce qu'on vient chercher sur la fiche
// d'un automate qui vient de tomber en panne, c'est : lesquelles sont en rejet,
// lesquelles attendent une suite, lesquelles n'ont même pas de cible. Le
// récapitulatif est donc servi par le moteur (il l'est déjà pour l'écran CQI)
// et non recompté ici.
import React, { useCallback, useEffect, useState } from 'react';
import { card, I, EmptyState } from '@/components/ui';
import { fmtDate } from '@/lib/meta';
import { TONS, type Resume } from './Cqi';

type Recap = { total: number; rejet: number; alerte: number; conforme: number; sansCible: number; enAttente: number };

const chip = (bg: string, fg: string): React.CSSProperties => ({
  fontSize: 10.5, fontWeight: 700, borderRadius: 'var(--radius-pill)', padding: '2px 9px', background: bg, color: fg,
});

export function CartesCqiEquipement({ equipementId, equipementNom }: {
  equipementId: string; equipementNom?: string;
}) {
  const [cartes, setCartes] = useState<Resume[] | null>(null);
  const [recap, setRecap] = useState<Recap | null>(null);
  const [erreur, setErreur] = useState<string | null>(null);

  const charger = useCallback(async () => {
    setErreur(null);
    const r = await fetch(`/api/ged/ged/cqi-cartes?equipement=${encodeURIComponent(equipementId)}`, { cache: 'no-store' });
    // Une réponse en échec est DITE. Retomber sur une liste vide donnerait
    // « cet automate ne porte aucun contrôle » — le pire message possible sur
    // un écran d'accréditation, et un constat fabriqué par un 403.
    if (!r.ok) {
      setErreur(`Les cartes de contrôle n’ont pas pu être lues (${r.status}).`);
      setCartes([]);
      return;
    }
    const d = await r.json();
    setCartes((d?.cartes as Resume[]) || []);
    setRecap((d?.recapitulatif as Recap) || null);
  }, [equipementId]);

  // ⚠️ `setState` synchrone dans un effet est une ERREUR de lint dans ce dépôt
  // (`react-hooks/set-state-in-effect`), et pas seulement une préférence : elle
  // déclenche des rendus en cascade. On repousse donc le chargement d'un tour
  // de boucle, comme le fait déjà `Cqi.tsx`, avec le drapeau qui empêche
  // d'écrire après un changement d'équipement.
  useEffect(() => {
    let vivant = true;
    void Promise.resolve().then(() => (vivant ? charger() : undefined));
    return () => { vivant = false; };
  }, [charger]);

  if (erreur) {
    return (
      <div style={{ ...card, padding: 16, fontSize: 12.5, borderInlineStart: '3px solid var(--red-600)' }}>
        <strong style={{ color: 'var(--red-600)' }}>{erreur}</strong>{' '}
        Ce n’est pas « aucun contrôle » : c’est une lecture qui a échoué.
      </div>
    );
  }
  if (cartes === null) {
    return <p style={{ margin: 0, fontSize: 12.5, color: 'var(--text-muted)' }}>Lecture des cartes de contrôle…</p>;
  }
  if (!cartes.length) {
    return (
      <EmptyState
        titre="Aucune carte de contrôle sur cet appareil"
        indice={'Une carte se crée depuis l’écran « Contrôle interne ». Tant que l’appareil n’en porte aucune, rien ne surveille ce qu’il rend.'}
      />
    );
  }

  return (
    <div style={{ display: 'grid', gap: 12 }}>
      <p style={{ margin: 0, fontSize: 12.5, color: 'var(--text-muted)' }}>
        État DÉRIVÉ à la lecture, par le même calcul que l’écran « Contrôle interne » —
        c’est la même donnée, pas une seconde évaluation. Il porte sur le lot de contrôle
        EN COURS : un lot refermé ne dit plus rien de ce que l’appareil produit aujourd’hui.
      </p>

      {recap && (
        <div style={{ ...card, padding: 12, display: 'flex', gap: 14, flexWrap: 'wrap', alignItems: 'center', fontSize: 12.5 }}>
          <span><strong style={{ color: 'var(--text-strong)' }}>{recap.total}</strong> carte(s)</span>
          <span style={chip(TONS.rejet.bg, TONS.rejet.fg)}>{recap.rejet} en rejet</span>
          <span style={chip(TONS.alerte.bg, TONS.alerte.fg)}>{recap.alerte} en avertissement</span>
          <span style={chip(TONS.conforme.bg, TONS.conforme.fg)}>{recap.conforme} conformes</span>
          {recap.sansCible > 0 && <span style={chip('var(--amber-50)', 'var(--amber-600)')}>{recap.sansCible} sans cible</span>}
          {recap.enAttente > 0 && (
            <span title="Passages non conformes sans dérogation ni repasse" style={chip('var(--red-50)', 'var(--red-600)')}>
              {recap.enAttente} passage(s) en attente de suite
            </span>
          )}
        </div>
      )}

      <div style={{ display: 'grid', gap: 6 }}>
        {cartes.map((c) => {
          const ton = TONS[c.statut] || TONS.conforme;
          return (
            <div key={c.carteId} style={{ ...card, padding: '9px 13px', display: 'flex', gap: 11, flexWrap: 'wrap', alignItems: 'center' }}>
              <span style={{ fontSize: 13, fontWeight: 700, color: 'var(--text-strong)', minWidth: 190 }}>{c.analyteNom}</span>
              <span style={{ fontSize: 12.5, color: 'var(--text-muted)' }}>{c.nbNiveaux} niveaux · {c.points} points</span>
              {c.aParametrer
                ? <span style={chip('var(--amber-50)', 'var(--amber-600)')} title="Sans période de lot ouverte, la carte n’a ni cible ni écart-type : aucune règle n’est évaluable.">Sans cible</span>
                : <span style={chip(ton.bg, ton.fg)}>{ton.label}</span>}
              {c.derniereRegle && <span style={{ fontSize: 11, fontFamily: 'var(--font-mono)', color: 'var(--text-muted)' }}>{c.derniereRegle}</span>}
              {c.enAttente > 0 && <span style={chip('var(--red-50)', 'var(--red-600)')}>{c.enAttente} en attente</span>}
              {c.sourceCible === 'interne' && <span style={chip('var(--cyan-50)', 'var(--cyan-700)')}>Cible interne</span>}
              <span style={{ flex: 1 }} />
              {c.lotControle && (
                <span title="Lot du MATÉRIAU DE CONTRÔLE — distinct du lot de réactif"
                  style={{ fontSize: 11, fontFamily: 'var(--font-mono)', color: 'var(--text-muted)' }}>
                  ctrl {c.lotControle}
                </span>
              )}
              {c.lotReactif && (
                <span title="Dernier lot de RÉACTIF saisi sur ce contrôle"
                  style={{ fontSize: 11, fontFamily: 'var(--font-mono)', color: 'var(--text-muted)' }}>
                  réactif {c.lotReactif}
                </span>
              )}
              <span style={{ fontSize: 12.5, color: 'var(--text-muted)' }}>{c.dernierPassage ? fmtDate(c.dernierPassage) : '—'}</span>
            </div>
          );
        })}
      </div>

      <p style={{ margin: 0, fontSize: 12.5, color: 'var(--text-muted)' }}>
        <I n="eye" s={12} /> Le détail d’une carte — graphe de Levey-Jennings, cibles, règles,
        historique — se lit sur l’écran « Contrôle interne »{equipementNom ? `, filtré sur ${equipementNom}` : ''}.
      </p>
    </div>
  );
}
